import {
  useContext,
  useEffect,
} from 'react';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import TextField from '@mui/material/TextField';
import Stack from '@mui/material/Stack';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import Box from '@mui/material/Box';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import Chip, { ChipProps } from '@mui/material/Chip';
import DoneIcon from '@mui/icons-material/Done';

import BarcodeInputBox from '#components/BarcodeInputBox';
import {
  DATE_FORMAT_STRING,
} from '#common/utils';
import {
  shippingInfoValidations,
} from '#common/validation';
import useForm from '#utils/use-form';
import { FeedbackUIContext } from '#context/feedback-ui-context';
import { EnsureSubscriptionComponent } from '#bootstrap/utils';
import { ADDONS } from '#common/constants/addons';
import ShippingComponent from '#addons/shipping/ShippingComponent';

interface BarcodeItem {
  barcode: string;
  received?: boolean;
  [key: string]: unknown;
}

interface Pkg {
  barcodeItems: string[];
  [key: string]: unknown;
}

interface ShippingInfo {
  trackingNumber?: string;
  shipper?: string;
  shipDate?: string | Date;
  barcodeItems: string[];
  [key: string]: unknown;
}

interface ShippingInfoFormValues {
  trackingNumber: string;
  shipper: string;
  shipDate: string | Date;
  barcodeItems: string[];
}

interface ShippingInfoPanelProps {
  barcodeItems: BarcodeItem[];
  shippingInfo?: ShippingInfo | undefined;
  packages: Pkg[];
  onSaveShippingInfo: (values: ShippingInfoFormValues) => void;
  userData?: unknown;
}

const buildInitialState = (shippingInfo?: ShippingInfo): Record<string, unknown> => {
  return (shippingInfo as unknown as Record<string, unknown>) || {
    trackingNumber: '',
    shipper: '',
    shipDate: new Date(),
    barcodeItems: [],
  };
};

export default function ShippingInfoPanel(props: ShippingInfoPanelProps) {
  const {
    barcodeItems,
    shippingInfo,
    packages,
    onSaveShippingInfo,
    userData,
  } = props;

  const {
    hideModal,
  } = useContext(FeedbackUIContext)!;

  const {
    values,
    setValues,
    isValid,
    errors,
    handleChange,
    handleSubmit,
    addSubmitListener,
    setTouched,
  } = useForm({
    initialState: buildInitialState(shippingInfo),
    validations: shippingInfoValidations,
  });

  const formValues = values as unknown as ShippingInfoFormValues;

  // Mark all fields as touched when the form opens
  useEffect(() => {
    setTouched({
      trackingNumber: true,
      shipper: true,
      shipDate: true,
      barcodeItems: true,
    });
  }, []);

  const handleSaveShippingInfo = (submitValues: Record<string, unknown>, valid: boolean) => {
    if (!valid) {
      return;
    }

    hideModal();
    onSaveShippingInfo(submitValues as unknown as ShippingInfoFormValues);
  };
  addSubmitListener(handleSaveShippingInfo);

  const toggleSelectedBarcodes = (newBarcode: { barcode: string | null }, toggle: boolean) => {
    let success = false;
    const code = newBarcode.barcode as string;
    const current = barcodeItems.find((bi) => bi.barcode === code && bi.received);
    if (!current) {
      // the barcodeItems should be received
      return success;
    }

    if (formValues.barcodeItems.includes(code) && toggle) {
      handleChange(null, {
        name: 'barcodeItems',
        value: formValues.barcodeItems.filter((si) => si !== code),
      });
      success = true;
    } else if (!formValues.barcodeItems.includes(code)) {
      handleChange(null, {
        name: 'barcodeItems',
        value: [
          code,
          ...formValues.barcodeItems,
        ],
      });
      success = true;
    }
    return success;
  };

  return (
    <>
      <FormControl sx={{ mb: 5 }}>
        <Stack spacing={2} direction="row" sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <TextField
            size="small"
            name="trackingNumber"
            label="Tracking Number"
            value={formValues.trackingNumber}
            error={Boolean(errors.trackingNumber)}
            onChange={(e) => handleChange(e)}
          />
          <TextField
            size="small"
            name="shipper"
            label="Shipper"
            value={formValues.shipper}
            error={Boolean(errors.shipper)}
            onChange={(e) => handleChange(e)}
          />
          <LocalizationProvider
            dateAdapter={AdapterDateFns}
          >
            <DesktopDatePicker
              label="Ship date"
              name="shipDate"
              format={DATE_FORMAT_STRING}
              value={new Date(formValues.shipDate)}
              slotProps={{
                textField: {
                  size: 'small',
                },
              }}
              sx={{ minWidth: 70 }}
              onChange={(d) => handleChange(null, {
                name: 'shipDate',
                value: (d as Date).toISOString(),
              })}
            />
          </LocalizationProvider>
        </Stack>

        <EnsureSubscriptionComponent addonId={ADDONS.SHIPPING}>
          <ShippingComponent
            userData={userData as Parameters<typeof ShippingComponent>[0]['userData']}
            onLabelCreated={(labelData: { trackingNumber: string }) => {
              setValues({
                ...values,
                trackingNumber: labelData.trackingNumber,
                shipper: 'UPS',
              });
            }}
          />
        </EnsureSubscriptionComponent>

        <Box sx={{ my: 2 }}>
          {
            barcodeItems
              .filter((itm) => itm.received)
              .filter((itm) => Object.keys(itm).length !== 1).map((itm) => {
                const selected = formValues.barcodeItems.includes(itm.barcode);

                let available = !packages
                  .some((p) => p.barcodeItems
                    .some((b) => b === itm.barcode));
                if (shippingInfo) {
                  available ||= shippingInfo.barcodeItems.includes(itm.barcode);
                }

                const disabled = !itm.received || !available;
                const color = disabled ? undefined : 'primary';
                const variant = selected ? '' : 'outlined';
                const icon = (selected || !available) ? <DoneIcon /> : undefined;

                return (
                  <Chip
                    key={itm.barcode}
                    disabled={disabled}
                    sx={{ m: 0.5 }}
                    label={itm.barcode}
                    color={color}
                    variant={variant as ChipProps['variant']}
                    icon={icon}
                    onClick={() => {
                      toggleSelectedBarcodes(itm, true);
                    }}
                  />
                );
              })
          }
        </Box>

        <Box>
          <BarcodeInputBox
            onSubmit={(event, newBarcode) => {
              const result = toggleSelectedBarcodes(
                newBarcode as { barcode: string | null },
                false,
              );
              if (result) {
                (event as { target: { value: string } }).target.value = '';
              }
            }}
          />
        </Box>
      </FormControl>

      <Stack spacing={2} direction="row" sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="outlined"
          onClick={hideModal}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!isValid}
          onClick={handleSubmit}
        >
          {shippingInfo ? 'Save' : 'Add'}
        </Button>
      </Stack>
    </>
  );
}
